import { LoaderCircle } from 'lucide-react';
import { lazy, Suspense, useLayoutEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AppShell } from './app/AppShell';
import { EmptyProject } from './features/creation/EmptyProject';
import { useAuth } from './auth/AuthProvider';
import { ChatProvider } from './chat/ChatProvider';
import { CallProvider } from './chat/calls/CallProvider';
import { LoginPage } from './pages/LoginPage';
import { WindowSession } from './app/WindowSession';
import './features/files/transfer-settings.css';
import { applyPageTheme } from './lib/pageTheme';
import { StudioPresenceProvider } from './features/presence/StudioPresence';
import { ActivityPresenceProvider } from './features/presence/ActivityPresence';

const AssetsPage = lazy(() => import('./pages/AssetsPage').then((module) => ({ default: module.AssetsPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((module) => ({ default: module.DashboardPage })));
const DialogsPage = lazy(() => import('./pages/DialogsPage').then((module) => ({ default: module.DialogsPage })));
const AttendancePage = lazy(() => import('./pages/AttendancePage').then((module) => ({ default: module.AttendancePage })));
const ExplorerPage = lazy(() => import('./features/files/Explorer').then(module => ({ default: module.ExplorerPage })));
const DiskrepkaPage = lazy(() => import('./features/files/DiskrepkaPage').then(module => ({ default: module.DiskrepkaPage })));
const UserAccessPage = lazy(() => import('./features/files/UserAccessPage').then(module => ({ default: module.UserAccessPage })));
const StorageSettingsPage = lazy(() => import('./features/files/StorageSettingsPage').then(module => ({ default: module.StorageSettingsPage })));
const TransferSettingsPage = lazy(() => import('./features/files/TransferSettingsPage').then(module => ({ default: module.TransferSettingsPage })));
const PublicSharePage = lazy(() => import('./features/files/PublicSharePage').then(module => ({ default: module.PublicSharePage })));
const PublicIntakePage = lazy(() => import('./features/files/PublicIntakePage').then(module => ({ default: module.PublicIntakePage })));

function PageFallback() {
  return <div className="app-loading"><LoaderCircle className="spin" size={30} /><span>Загрузка…</span></div>;
}

function RequireAuth({ filesOnly = false }: { filesOnly?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="app-loading"><LoaderCircle className="spin" size={30} /><span>Загрузка skrepka…</span></div>;
  }
  if (!user) return <Navigate replace state={{ from: location.pathname + location.search }} to="/auth/login" />;
  if (user.chatOnly && location.pathname !== '/dialogs') return <Navigate replace to="/dialogs" />;
  if (filesOnly) return <ActivityPresenceProvider key={user.id}><StudioPresenceProvider><DiskrepkaPage/></StudioPresenceProvider></ActivityPresenceProvider>;
  return <ActivityPresenceProvider key={user.id}><StudioPresenceProvider><WindowSession key={user.id} userId={user.id}><ChatProvider><CallProvider><AppShell /></CallProvider></ChatProvider></WindowSession></StudioPresenceProvider></ActivityPresenceProvider>;
}

function ProjectRedirect() {
  const { user } = useAuth();
  const { projectId } = useParams();
  const project = user?.projects?.find((item) => item.id === Number(projectId));
  const storedId = Number(localStorage.getItem(`project_last_regulation_${projectId}`));
  const regulation = project?.regulations.find((item) => item.id === storedId) ?? project?.regulations[0];
  return project && regulation
    ? <Navigate replace to={`/projects/${project.id}/assets/${regulation.id}`} />
    : project ? <EmptyProject projectId={project.id}/> : <Navigate replace to="/dashboard/active" />;
}

function AssetsRoute() {
  const { user } = useAuth();
  const { projectId, regulationId } = useParams();
  const project = user?.projects?.find((item) => item.id === Number(projectId));
  if (project && !project.regulations.some((item) => item.id === Number(regulationId))) {
    return <Navigate replace to={`/projects/${project.id}`} />;
  }
  return <AssetsPage key={`${projectId}:${regulationId}`} />;
}

function DashboardRoute() {
  const { tab } = useParams();
  return <DashboardPage key={tab} />;
}

export function App() {
  const { pathname } = useLocation();
  useLayoutEffect(() => { applyPageTheme(pathname); }, [pathname]);
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<LoginPage />} path="/auth/login" />
        <Route element={<PublicSharePage />} path="/s/:token" />
        <Route element={<PublicIntakePage />} path="/s/inbox/:token" />
        <Route element={<PublicIntakePage />} path="/s/u/:token" />
        <Route element={<RequireAuth filesOnly/>} path="/diskrepka"/>
        <Route element={<RequireAuth />}>
          <Route element={<Navigate replace to="/dashboard/active" />} path="/" />
          <Route element={<Navigate replace to="/dashboard/active" />} path="/dashboard" />
          <Route element={<DashboardRoute />} path="/dashboard/:tab" />
          <Route element={<ProjectRedirect />} path="/projects/:projectId" />
          <Route element={<AssetsRoute />} path="/projects/:projectId/assets/:regulationId" />
          <Route element={<DialogsPage />} path="/dialogs" />
          <Route element={<AttendancePage />} path="/attendance-v2" />
          <Route element={<ExplorerPage />} path="/files" />
          <Route element={<UserAccessPage />} path="/settings/users" />
          <Route element={<StorageSettingsPage />} path="/settings/storage" />
          <Route element={<TransferSettingsPage />} path="/settings/transfers" />
          <Route element={<Navigate replace to="/dashboard/active" />} path="*" />
        </Route>
      </Routes>
    </Suspense>
  );
}
